import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { EntityMap } from '@shared/models/types';
import { PreSignedUrl } from '@control-tower/models/jpm/pre-signed-url';
import { FileHandlerService } from '@shared/services/file-handler.service';
import { FetchCollaboratorPhotos, FetchCollaboratorData } from './collaborators.actions';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { SetNotificationAction } from '@core/store/notification/notification.actions';
import { AlertDialogType } from '@shared/models';
import { of } from 'rxjs';
import { SharedJobService } from 'app/modules/job-live-map/services/shared-job.service';
import { OrgUser } from 'app/modules/organization/models/org-user';

export interface CollaboratorsStateModel {
  collaboratorPhotos: EntityMap<string, string>; // photoId -> presignedUrl
  collaboratorData: EntityMap<string, OrgUser>; // userId -> OrgUser
}

export const collaboratorsStateKey = 'collaborators';

export const getInitialCollaboratorsState = (): CollaboratorsStateModel => ({
  collaboratorPhotos: {},
  collaboratorData: {}
});

@State<CollaboratorsStateModel>({
  name: collaboratorsStateKey,
  defaults: getInitialCollaboratorsState()
})
@Injectable()
export class CollaboratorsState {
  constructor(private fileHandlerService: FileHandlerService, private sharedJobService: SharedJobService) {}

  @Selector()
  static getCollaboratorPhotos(state: CollaboratorsStateModel): EntityMap<string, string> {
    return state.collaboratorPhotos;
  }

  @Selector()
  static getCollaboratorData(state: CollaboratorsStateModel): EntityMap<string, OrgUser> {
    return state.collaboratorData;
  }

  @Action(FetchCollaboratorPhotos)
  fetchCollaboratorPhotos(
    { patchState, getState, dispatch }: StateContext<CollaboratorsStateModel>,
    { photoIds }: FetchCollaboratorPhotos
  ) {
    if (!photoIds || photoIds.length === 0) {
      return of(null);
    }

    // Filter out null/undefined and already fetched photos
    const state = getState();
    const photosToFetch = photoIds.filter(
      id => id && !state.collaboratorPhotos[id]
    );

    if (photosToFetch.length === 0) {
      return of(null);
    }

    return this.fileHandlerService.fetchFiles(photosToFetch).pipe(
      map((presignedUrls: PreSignedUrl[]) => {
        const newPhotos: EntityMap<string, string> = {};
        presignedUrls.forEach((presignedUrl: PreSignedUrl) => {
          if (presignedUrl.id && presignedUrl.url) {
            newPhotos[presignedUrl.id] = presignedUrl.url;
          }
        });

        patchState({
          collaboratorPhotos: {
            ...state.collaboratorPhotos,
            ...newPhotos
          }
        });
      }),
      catchError((error) => {
        console.error('Error fetching collaborator photos:', error);
        return dispatch(
          new SetNotificationAction(
            'Error loading collaborator photos',
            AlertDialogType.ERROR
          )
        );
      })
    );
  }

  @Action(FetchCollaboratorData)
  fetchCollaboratorData(
    { patchState, getState, dispatch }: StateContext<CollaboratorsStateModel>,
    { collaboratorUserIds }: FetchCollaboratorData
  ) {
    if (!collaboratorUserIds || collaboratorUserIds.length === 0) {
      return of(null);
    }

    // Filter out null/undefined/empty IDs
    const filteredUserIds = collaboratorUserIds.filter(id => id != null && id !== '');
    if (filteredUserIds.length === 0) {
      return of(null);
    }

    // Fetch collaborator details using getSelectedJpmHumanResources (which uses getOrgPersonsByIds API)
    return this.sharedJobService.getSelectedJpmHumanResources(filteredUserIds).pipe(
      mergeMap((orgUsers: OrgUser[]) => {
        // Store orgUsers in state for mapping user IDs to OrgUser objects
        const state = getState();
        const existingData = { ...state.collaboratorData };
        orgUsers.forEach(user => {
          if (user.id) {
            existingData[user.id] = user;
          }
        });
        patchState({ collaboratorData: existingData });

        // Extract photo IDs from the returned OrgUser objects
        const photoIds = orgUsers
          .map(user => user.photo)
          .filter(photoId => photoId != null && photoId !== '');

        // Dispatch FetchCollaboratorPhotos to get presigned URLs
        if (photoIds.length > 0) {
          return dispatch(new FetchCollaboratorPhotos(photoIds));
        }

        return of(null);
      }),
      catchError((error) => {
        console.error('Error fetching collaborator data:', error);
        return dispatch(
          new SetNotificationAction(
            'Error loading collaborator data',
            AlertDialogType.ERROR
          )
        );
      })
    );
  }
}

